import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatWidget } from "@cx3/sdk";
import { useAuth } from "@/hooks/use-auth";
import { fetchUserById } from "@/api/user";
import { CHAT_CONFIG, getChatToken, resolveUser } from "@/lib/chat";
import ViewProfile from "./profile";

export default function ChatPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { astrologer } = useAuth();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetchUserById(userId, astrologer.accessToken);
        setUser({
          id: response._id,
          name: response.name,
          image: response.picture,
        });
      } catch (err) {
        console.error("Error fetching user:", err);
        setError("Failed to load user details");
      } finally {
        setLoading(false);
      }
    };
    
    if (userId && astrologer?.accessToken) {
      fetchUserInfo();
    }
  }, [userId, astrologer?.accessToken]);

  // Token fetcher for chat
  const getToken = useCallback(async () => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) {
      throw new Error("Astrologer not authenticated");
    }
    return getChatToken(astrologer.astrologerId, astrologer.accessToken);
  }, [astrologer?.astrologerId, astrologer?.accessToken]);

  const toggleProfile = () => setShowProfile(!showProfile);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !astrologer?.astrologerId) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-4">
        <div className="text-red-500 mb-2">
          <svg className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium">Error</h3>
        <p className="text-gray-600 text-center mt-1">{error || "Authentication required"}</p>
        <button
          onClick={() => navigate("/interact/chat")}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-md"
        >
          Go Back
        </button>
      </div>
    );
  }

  const chatConfig = {
    serverUrl: CHAT_CONFIG.serverUrl,
    tenantId: CHAT_CONFIG.tenantId,
    userId: `astro_${astrologer.astrologerId}`,
    userRole: "provider",
    recipientId: `user_${userId}`,
    getToken,
    resolveUser,
  };

  // Custom header
  const customHeader = (
    <div className="flex flex-row justify-between items-center w-full">
      <div className="flex items-center">
        <div className="md:hidden mr-2">
          <button
            onClick={() => navigate("/interact/chat")}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        
        <div className="w-10 h-10 rounded-full bg-[#F15A29] flex items-center justify-center text-white font-semibold text-lg">
          {user?.name?.charAt(0).toUpperCase() || "U"}
        </div>
        <div className="ml-3">
          <h3 className="font-medium">{user?.name || "User"}</h3>
        </div>
      </div>
      
      <button
        onClick={toggleProfile}
        className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium py-2 px-4 rounded-full"
      >
        {showProfile ? "Hide profile" : "View profile"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#FEF0E7] relative overflow-hidden">
      {/* Flexible Content Area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Chat Widget */}
        <div className={`flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden ${showProfile ? 'w-full md:w-3/4' : 'w-full'}`}>
          <ChatWidget
            config={chatConfig}
            header={customHeader}
            showConnectionStatus={true}
            showTypingIndicator={true}
            showReadReceipts={true}
            showOnlineStatus={true}
            placeholder="Type here..."
            className="flex-1"
            style={{
              border: 'none',
              borderRadius: '0',
              height: '100%',
              backgroundColor: '#FEF0E7',
            }}
            emptyState={
              <div className="flex items-center justify-center h-full">
                <p className="text-[#8B5A44] bg-[#F5DAC8] px-4 py-2 rounded-full text-sm">
                  No messages yet. Start the conversation!
                </p>
              </div>
            }
          />
        </div>

        {/* Profile Panel */}
        {showProfile && user && (
          <div className="md:relative fixed md:static top-0 right-0 h-full bg-white shadow-lg w-full md:w-1/4 z-10 transition-all duration-300 ease-in-out">
            <ViewProfile user={user} onClose={toggleProfile} />
          </div>
        )}
      </div>
    </div>
  );
}
